import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import moment from 'moment'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { Tooltip } from '@mui/material'
import lang from '@/lang'
import config from '@/config'
import ThemeInterface from '@/theme-interface'
import { DateInput, TextInput } from '@/modules/base/components/inputs'
import { getPayKinds } from '@/modules/base/helpers/getSettingsData'
import { resolveDocuAiPayKindId } from '@/modules/expense/components/edit/payment-conditions/docuai-pay-kind'
import Badge from '@/modules/base/components/badge/badge'
import {
  getFieldValidation,
  getPayeeAccountValidation,
  FieldMetaMap,
  PayeeAccountMeta,
} from '@/modules/document/components/review/field-validation'

// FLAG:useDocuAi — editable review payment fields (v2). The paid-status/payment values
// DocuAI reads off the document (open amount, paid status, payment method, the stated
// payee IBAN, the printed card) used to be a read-only hint banner; they are now
// reviewer-editable fields that persist into documentInvoiceDetails on save.
//
// v3 (designer layout): status + open amount share a row, method and IBAN take the full
// width, card number + issuer share the last row. When the document states MORE THAN ONE
// payee IBAN the single field becomes an explicit choice — a banner plus one radio card
// per recognized account — because picking the wrong payment account is a payment that
// lands at the wrong bank, not a cosmetic detail. The chosen IBAN is what the save writes
// to documentInvoiceDetails.iban, and from there onto the supplier/customer master record
// (receiptReviewCapabilities: createSnapshotParty / mergeReviewedBankAccount).
//
// v6 (issue bc3e6bbc): every recognized account carries its OWN bank block. DocuAI ships
// {iban, bic, bankName, currency} per account (contract 1.7.0) into
// documentInvoiceDetails.payeeAccounts, index-aligned with payeeIbans, so the bank line is no
// longer pinned to card 0 — and the account whose PRINTED currency is not the invoice's gets
// the same yellow review color as everything else the reviewer should look at. Picking (or
// correcting) a card adopts that account's bank/BIC into the editable pair, because the pair
// describes the SELECTED account and would otherwise keep describing the previous one. The
// list operations keep payeeAccounts index-aligned with payeeIbans; a document without the
// ingested list behaves exactly as it did in v5.
//
// v5: the recognized list is REVIEWER-OWNED. Correcting a card rewrites its entry and the
// trash icon drops the account entirely (the scan does surface footer/imprint accounts that
// are not the payee), and the curated list is persisted back to the EXISTING
// documentInvoiceDetails.payeeIbans column — no new column, no migration. `data.payeeIbans`
// (seeded by build-review-data) is therefore the authoritative list; the ingested prop is
// only the fallback for a row stored before the list became editable.
//
// Rendered only inside the shared ReceiptEdit review mode, so it inherits the one central
// review surface; legacy expense/revenue editing never mounts it.

const PAYMENT_METHOD_CODES = ['credit_transfer', 'direct_debit', 'card', 'cash', 'paypal']
const PAYMENT_STATUS_CODES = ['paid', 'partially_paid', 'unpaid']

// The reviewer's own IBAN, when it is not one of the recognized ones (a legacy row, or a
// value typed before this choice existed). It still gets a card so nothing is hidden.
const CUSTOM_IBAN_KEY = '__custom__'

// Paid-status → Badge chip (same chip vocabulary as the receipt-state "Offen" badge).
const STATUS_BADGE: Record<string, { color: string }> = {
  paid: { color: 'green' },
  partially_paid: { color: 'orange' },
  unpaid: { color: 'blue' },
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  // Sits inside the Payment card, below the payment actions — a light top
  // divider ties it to the card rather than a separate white box.
  root: {
    marginTop: 20,
    paddingTop: 20,
    borderTop: theme.border,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  },
  title: {
    fontWeight: 600,
    fontSize: theme.fontSizes?.medium ?? 15,
    color: theme.black,
  },
  statusChip: {
    fontSize: theme.fontSizes?.small ?? 12,
  },
  // The section is a stack of rows; only some of them split into two columns.
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 16,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontSize: theme.fontSizes?.small ?? 12,
    color: theme.grey,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  // A hard check failure ("value looks wrong").
  errorLabel: {
    color: theme.red,
    fontWeight: 600,
  },
  // The soft "please check" review color — also used for a low-confidence value on a
  // legacy envelope that carries no verdict. Never red: nothing has actually failed.
  warningLabel: {
    color: theme.yellow,
    fontWeight: 600,
  },
  checkHint: {
    fontWeight: 400,
    fontSize: theme.fontSizes?.tiny ?? 10,
    cursor: 'help',
  },
  errorHint: {
    color: theme.red,
  },
  warningHint: {
    color: theme.yellow,
  },
  select: {
    height: 38,
    borderRadius: 4,
    border: theme.border,
    padding: '0 8px',
    background: theme.white,
    color: theme.black,
    fontSize: theme.fontSizes?.small ?? 12,
  },
  // Read-only direct-debit-account hint under the payee IBAN field.
  debitHint: {
    fontSize: theme.fontSizes?.tiny ?? 10,
    color: theme.grey,
    marginTop: 2,
  },
  // Reassuring variant when the printed debit account matches a tenant account.
  debitHintMatch: {
    color: theme.green,
    fontWeight: 600,
  },
  // An IBAN that failed the format/mod-97 check, or that the analysis flagged: the
  // field itself carries the review color so the value and its verdict stay together.
  ibanFieldWarning: {
    '& input': {
      borderColor: theme.yellow,
      background: theme.white,
    },
  },
  ibanFieldError: {
    '& input': {
      borderColor: theme.red,
      background: theme.white,
    },
  },
  // The warning icon plus the "Bitte IBAN prüfen" label under the field — never red (the check is
  // deliberately conservative and must not look like a rejected save).
  fieldCheck: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 6,
    fontSize: theme.fontSizes?.small ?? 12,
    color: theme.yellow,
    fontWeight: 500,
  },
  fieldCheckError: {
    color: theme.red,
  },
  checkIcon: {
    fontSize: 16,
  },
  // Yellow banner above the IBAN choice: several accounts were recognized and the
  // reviewer has to say which one this invoice is paid to.
  choiceBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 14px',
    borderRadius: theme.borderRadius,
    background: theme.yellowBackground,
    color: theme.black,
    fontSize: theme.fontSizes?.small ?? 12,
    lineHeight: 1.45,
    marginBottom: 12,
  },
  choiceBannerIcon: {
    fontSize: 18,
    color: theme.yellow,
    flexShrink: 0,
  },
  choiceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  choiceCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 12,
    border: `1px solid ${theme.borderColor}`,
    borderRadius: theme.borderRadius,
    background: theme.white,
    cursor: 'pointer',
  },
  choiceCardSelected: {
    borderColor: theme.primary,
    background: theme.lightBlueBackground,
  },
  choiceCardReadOnly: {
    cursor: 'default',
  },
  choiceRadio: {
    marginTop: 12,
    accentColor: theme.primary,
    cursor: 'inherit',
  },
  choiceBody: {
    flex: 1,
    minWidth: 0,
  },
  // Plain-text action under a lone recognized IBAN the field no longer matches.
  adoptRecognized: {
    alignSelf: 'flex-start',
    marginTop: 6,
    padding: 0,
    border: 'none',
    background: 'none',
    color: theme.primary,
    fontSize: theme.fontSizes?.small ?? 12,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  // "Commerzbank AG · COBADEFFXXX" — only rendered for the account the document
  // actually carries bank details for; nothing is inferred for the others.
  choiceMeta: {
    fontSize: theme.fontSizes?.small ?? 12,
    color: theme.grey,
  },
  // Bank details on the left, the delete action on the right — the meta line is optional,
  // so the button is pushed right by its own margin rather than by space-between.
  choiceFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 6,
    minHeight: 20,
  },
  deleteIban: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    padding: 2,
    border: 'none',
    background: 'none',
    color: theme.primary,
    cursor: 'pointer',
    lineHeight: 0,
  },
  deleteIcon: {
    fontSize: 18,
  },
}))

interface PaymentDetails {
  paymentStatus?: string | null
  openAmount?: number | string | null
  paymentMethodCode?: string | null
  iban?: string | null
  // FLAG:useDocuAi — Bank/BIC of the payment account. Seeded by build-review-data from
  // documentInvoiceDetails and reviewer-editable since 2026-08-11: on a multi-account
  // invoice the ingested pair can belong to a different account than the one the reviewer
  // chooses, and there was no way to correct it (issue bc3e6bbc).
  bic?: string | null
  bankInstitute?: string | null
  payeeIbans?: string[] | null
  // FLAG:useDocuAi — the ingested bank block PER recognized account (contract 1.7.0),
  // index-aligned with payeeIbans. Not typed by the reviewer, but carried in the form data
  // and kept aligned by the list operations below: correcting or deleting a card moves its
  // bank line and its currency hint with it.
  payeeAccounts?: PayeeAccountMeta[] | null
  // FLAG:useDocuAi — buyer-side debit accounts, reviewer-owned like payeeIbans: the curated
  // list is saved back onto documentInvoiceDetails.debitIbans (no new column, no migration).
  debitIbans?: string[] | null
  paymentCardNumber?: string | null
  paymentCardScheme?: string | null
  // v4: both moved here from the retired "Zahlungskonditionen" card — the due date as the
  // section's last field, and the orgaMAX payment type which the payment-method select now derives.
  dueDate?: string | Date | null
  payKindId?: number | string | null
}

interface ReviewPaymentFieldsProps {
  // Current form values (data) for the editable fields.
  data: PaymentDetails
  // The persisted ingested details — the recognized-IBANs preview plus the
  // per-field validation metadata (fieldMeta) driving the "please check" marks.
  documentInvoiceDetails?: {
    payeeIbans?: string[] | null
    // Bank details of the account the document names as THE payment account
    // (paymentDetails.bic / .bankInstitute → vendor.bankAccounts[0]) — i.e. of the
    // SELECTED account. The further accounts get theirs from `payeeAccounts` below;
    // before that existed they had none at all and the bank line was pinned to card 0.
    bic?: string | null
    bankInstitute?: string | null
    // FLAG:useDocuAi — {iban, bic, bankName, currency, validation…} per recognized
    // account (DocuAI contract 1.7.0), index-aligned with payeeIbans. The ingested seed
    // for the form value above; null on a document/contract that carries none, in which
    // case the section behaves exactly as it did before this list existed.
    payeeAccounts?: PayeeAccountMeta[] | null
    // Buyer-side SEPA direct-debit account(s) as printed (usually masked) — the
    // ingested seed for the editable field, used until `data.debitIbans` carries the
    // reviewer's own list.
    debitIbans?: string[] | null
    // Present when a printed debit IBAN matches one of the tenant's own bank
    // accounts (attached in serialize.js) — a reassuring "the debit matches your
    // stored account …" instead of the raw IBAN.
    debitAccountMatch?: { iban?: string | null; debitIban?: string | null } | null
    fieldMeta?: FieldMetaMap | null
    extractionConfidence?: Record<string, number> | null
    // FLAG:useDocuAi — server-owned §3.5 gate: 'xml' means the values were mapped
    // from a legally-binding XML e-invoice. Drives whether the recognized-IBANs
    // choice is selectable (OCR) or display-only (XML). Product decision 2026-07-05:
    // pick a recognized seller IBAN on OCR, not on XML.
    reviewSource?: { source?: string | null } | null
  } | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (patch: Record<string, any>) => void
  readOnly?: boolean
}

const normalizeIban = (iban?: string | null) => `${iban ?? ''}`.replace(/\s+/g, '').toUpperCase()

const formatIban = (iban: string) => normalizeIban(iban).replace(/(.{4})/g, '$1 ').trim()

const dedupeIbans = (ibans: string[]) => {
  const seen = new Set<string>()
  return ibans.filter((iban) => {
    const normalized = normalizeIban(iban)
    if (!normalized || seen.has(normalized)) return false
    seen.add(normalized)
    return true
  })
}

// FLAG:useDocuAi — lightweight client IBAN check (ISO 13616 format + mod-97). Not a full
// per-country length table, so it is intentionally conservative: it only reports invalid on a
// clearly-broken value (bad format or failing mod-97), so it never blocks an IBAN the server
// (ibantools) would accept. It catches the common typo that otherwise makes the server reject
// the ENTIRE review save and silently drop the reviewer's other edits.
export const isLikelyValidIban = (raw: string): boolean => {
  const iban = `${raw}`.replace(/\s+/g, '').toUpperCase()
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  const numeric = rearranged.replace(/[A-Z]/g, ch => `${ch.charCodeAt(0) - 55}`)
  let remainder = 0
  for (let i = 0; i < numeric.length; i += 7) {
    remainder = Number(`${remainder}${numeric.slice(i, i + 7)}`) % 97
  }
  return remainder === 1
}

const ReviewPaymentFields = ({ data, documentInvoiceDetails, onChange, readOnly = false }: ReviewPaymentFieldsProps) => {
  const classes = useStyles()
  const fields = lang.documents.reviewPaymentStatus.fields
  const ibanChoiceLang = lang.documents.reviewPaymentStatus.ibanChoice
  // v5 — the reviewer's curated list wins over the ingested one. Only the ingested list is
  // deduped: once the reviewer owns it, every entry stays exactly as they left it (deduping
  // a reviewer list would make a card vanish mid-keystroke the moment its value briefly
  // matches another one).
  const recognizedIbans = Array.isArray(data.payeeIbans)
    ? data.payeeIbans.filter(Boolean)
    : (Array.isArray(documentInvoiceDetails?.payeeIbans)
      ? dedupeIbans(documentInvoiceDetails!.payeeIbans!.filter(Boolean))
      : [])
  // v4 — the "Zahlungskonditionen" card is gone from the review (its payment type said the same as
  // the payment method here, its due date belonged with the payment values). So this select has to
  // carry the booking's orgaMAX payment type too: the same title map the retired card used.
  const [payKinds, setPayKinds] = useState<Array<{ id: number, title: string }>>([])

  useEffect(() => {
    getPayKinds().then(({ body: { data } }) => setPayKinds(data)).catch(() => setPayKinds([]))
  }, [])

  // Seed the payment type once from the document's stated method, exactly as the retired card did
  // (guarded on an empty payKindId, so a reviewer's own pick is never overruled).
  useEffect(() => {
    if (readOnly || !payKinds.length || data.payKindId || !data.paymentMethodCode) return
    const payKindId = resolveDocuAiPayKindId(payKinds, data.paymentMethodCode)
    if (payKindId === undefined) return
    onChange({ payKindId })
  }, [payKinds, data.payKindId, data.paymentMethodCode, readOnly])

  const handleMethodChange = (code: string | null) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const patch: Record<string, any> = { paymentMethodCode: code }
    const payKindId = code ? resolveDocuAiPayKindId(payKinds, code) : undefined
    // Only when the method maps onto one of the tenant's payment types — an unmapped code must not
    // silently clear a payment type the reviewer already has.
    if (payKindId !== undefined) patch.payKindId = payKindId
    onChange(patch)
  }
  const normalizedPrimaryIban = normalizeIban(data.iban)
  // The recognized-IBANs list is a real choice on scanned (OCR) invoices — picking
  // one corrects a mis-extracted seller IBAN — but display-only on XML e-invoices
  // (legally binding) and whenever the form is read-only. Derived from the ingested
  // row's server-owned reviewSource, so no extra prop wiring from the parent is needed.
  const xmlLocked = documentInvoiceDetails?.reviewSource?.source === 'xml'
  const canPickIban = !readOnly && !xmlLocked
  // SEPA direct debit: the buyer-side account being debited (usually masked). Shown in its
  // own field below the payee IBAN so the recognized debit account is never invisible just
  // because there is no payee IBAN to pay to (product decision 2026-07-05), and editable
  // like every other review value. `data` first, exactly like the payee list: once the
  // reviewer has touched it, their list — including an emptied one — is authoritative and
  // must not be overwritten by the ingested seed.
  const debitIbans = Array.isArray(data.debitIbans)
    ? data.debitIbans.filter(Boolean)
    : (Array.isArray(documentInvoiceDetails?.debitIbans)
      ? documentInvoiceDetails!.debitIbans!.filter(Boolean)
      : [])
  const debitAccountMatch = documentInvoiceDetails?.debitAccountMatch
  // The payment-details section must only show what the document actually prints:
  // the (usually masked) debit IBAN — NOT the tenant's full stored IBAN it happens
  // to match. Use the matched document IBAN (match.debitIban), never match.account.iban.
  const debitMatchIban = debitAccountMatch?.debitIban || null

  // FLAG:useDocuAi — review-component binding: mark exactly the fields a validation
  // check flagged (or that were extracted with low confidence). The validationNote
  // rides along as the tooltip so the reviewer knows WHY to check.
  const fieldMeta = documentInvoiceDetails?.fieldMeta
  const extractionConfidence = documentInvoiceDetails?.extractionConfidence
  const renderLabel = (field: string, text: string) => {
    const { treatment, note } = getFieldValidation(fieldMeta, extractionConfidence, field)
    // The graded verdict decides the colour: 'error' red, 'warning' the yellow
    // review color, 'ok'/'info' nothing. Previously every flagged-or-low-confidence
    // field went red, which made a soft "please check" look like a failed check.
    return (
      <span className={clsx(
        classes.label,
        treatment === 'error' && classes.errorLabel,
        treatment === 'warning' && classes.warningLabel,
      )}>
        {text}
        {treatment && (
          <span
            className={clsx(
              classes.checkHint,
              treatment === 'error' ? classes.errorHint : classes.warningHint,
            )}
            data-testid={`review-payment-check-${field}`}
            // Which grade is shown, without depending on the generated JSS class name.
            data-state={treatment}
            title={note || lang.documents.reviewPaymentStatus.lowConfidence}
          >
            {lang.documents.reviewPaymentStatus.checkHint || '⚠'}
          </span>
        )}
      </span>
    )
  }

  // The IBAN's verdict, merged from the two independent sources: the typed value's own
  // format/mod-97 check, and the analysis' per-field validation. The format check wins
  // because it describes the value on screen RIGHT NOW; the analysis verdict describes
  // what was extracted. `includeMeta` is false for the non-selected choice cards — the
  // analysis only ever judged the account it read as THE payment account.
  const ibanCheckFor = (value: string, includeMeta: boolean) => {
    if (value && !isLikelyValidIban(value)) {
      return { treatment: 'warning' as const, note: lang.documents.reviewPaymentStatus.ibanInvalid }
    }
    if (!includeMeta) return { treatment: null, note: null }
    const { treatment, note } = getFieldValidation(fieldMeta, extractionConfidence, 'iban')
    return { treatment, note }
  }

  // `label` overrides the default "Bitte IBAN prüfen" wording — the currency hint below is
  // the same visual treatment but says something else entirely.
  const renderIbanCheck = (
    treatment: string | null, note: string | null, testId: string, label?: string | null,
  ) => {
    if (!treatment) return null
    return (
      <div
        className={clsx(classes.fieldCheck, treatment === 'error' && classes.fieldCheckError)}
        data-testid={testId}
        data-state={treatment}
        title={note || undefined}
      >
        <WarningAmberRoundedIcon className={classes.checkIcon} />
        <span>{label || ibanChoiceLang.checkHint}</span>
      </div>
    )
  }

  const ibanFieldClass = (treatment: string | null) => clsx(
    treatment === 'warning' && classes.ibanFieldWarning,
    treatment === 'error' && classes.ibanFieldError,
  )

  // FLAG:useDocuAi — the ingested bank block per recognized account (contract 1.7.0),
  // index-aligned with `recognizedIbans`. `data` first for the same reason as the IBAN list
  // itself: once the reviewer has corrected or deleted a card, THEIR list is authoritative
  // and the two must move together.
  const payeeAccounts = Array.isArray(data.payeeAccounts)
    ? data.payeeAccounts
    : (Array.isArray(documentInvoiceDetails?.payeeAccounts)
      ? documentInvoiceDetails!.payeeAccounts!
      : [])
  const hasPayeeAccounts = payeeAccounts.length > 0
  const accountFor = (index: number): PayeeAccountMeta | null => (
    index >= 0 ? (payeeAccounts[index] ?? null) : null
  )
  // Keep the account rows in step with an edit/removal on the IBAN list. Nothing is
  // recomputed from the new value — the bank block describes the account the reviewer is
  // correcting, so it travels with the card, exactly as the position-keyed lookup below
  // assumes. Returns undefined when there is nothing to carry, so the patch stays absent
  // and a document without the ingested list is untouched.
  const alignedAccounts = (mapper: (accounts: PayeeAccountMeta[]) => PayeeAccountMeta[]) => (
    hasPayeeAccounts ? mapper(payeeAccounts) : undefined
  )

  // Bank details belong to the account they were read FOR. Keyed by POSITION, so a
  // correction keeps them instead of dropping them the moment the string stops matching.
  //  * the SELECTED card shows the editable pair (data.bankInstitute/.bic) — that is the
  //    one the reviewer can correct, and it is what the save writes;
  //  * every other card shows its OWN ingested bank/BIC. Until contract 1.7.0 the contract
  //    carried none for them, which is why the line used to be pinned to card 0 with the
  //    note that "nothing is inferred for the others" — there was nothing TO show;
  //  * without any ingested accounts (legacy row) the old rule stands unchanged: the pair
  //    describes the document's one payment account and is printed at card 0.
  const bankMetaFor = (index: number, selected: boolean) => {
    // The reviewer's own card (index < 0) is not one of the recognized accounts, so
    // nothing is known about its bank — the editable Bank/BIC fields below are where
    // that value lives, and echoing them here would just repeat what is on screen.
    if (index < 0) return null
    const account = accountFor(index)
    const useFormPair = selected || (!hasPayeeAccounts && index === 0)
    const formPair = [
      data.bankInstitute ?? documentInvoiceDetails?.bankInstitute,
      data.bic ?? documentInvoiceDetails?.bic,
    ]
    const meta = (useFormPair ? formPair : [account?.bankName, account?.bic]).filter(Boolean)
    return meta.length ? meta.join(' · ') : null
  }

  // FLAG:useDocuAi — the foreign-currency hint. DocuAI marks the account whose PRINTED
  // currency is not the invoice's (rule `payee_account_currency`), and its note names both
  // codes. This is the per-card counterpart to the flat fieldMeta marks: a non-selected card
  // could carry no verdict at all before, because fieldMeta has one slot for 'iban'.
  const currencyCheckFor = (index: number) => {
    const account = accountFor(index)
    const { treatment } = getPayeeAccountValidation(account)
    if (!treatment) return null
    return {
      treatment,
      note: account?.validationNote
        || ibanChoiceLang.foreignCurrency(account?.currency ?? ''),
    }
  }

  // Selecting an account does not change the list — but it DOES change which account the
  // document's bank pair describes. Adopting the picked account's ingested bank/BIC is the
  // point of the whole change: the reviewer picks the second (USD) account and its bank and
  // BIC appear, instead of the first account's values silently staying put next to it.
  // Only when that account HAS an ingested row: without one nothing is known about it, and
  // clearing the pair would throw away a value the reviewer may have typed themselves.
  const selectIban = (value: string, index = -1) => {
    if (!canPickIban) return
    const account = accountFor(index)
    onChange({
      iban: value || null,
      ...(account ? { bankInstitute: account.bankName ?? null, bic: account.bic ?? null } : {}),
    })
  }

  // Editing a card is itself a choice — the reviewer corrected THAT account because it is
  // the one to pay. Committing the edit without also selecting it would leave the typed
  // value visible but unused, which is exactly the silent data loss the review must not have.
  // The correction also replaces the entry IN the list, so reopening the review shows what
  // the reviewer typed rather than the mis-extracted original plus a stray "custom" card.
  const editIban = (index: number, value: string) => {
    if (!canPickIban) return
    const accounts = alignedAccounts(list => list.map((account, i) => (
      i === index ? { ...account, iban: value } : account
    )))
    const account = accountFor(index)
    onChange({
      payeeIbans: recognizedIbans.map((iban, i) => i === index ? value : iban),
      iban: value || null,
      ...(accounts ? { payeeAccounts: accounts } : {}),
      // Same adoption as picking the card: correcting a card selects it, so the pair must
      // describe THAT account rather than the one that happened to be selected before.
      ...(account ? { bankInstitute: account.bankName ?? null, bic: account.bic ?? null } : {}),
    })
  }

  // The account scan reports every IBAN on the page, including footer/imprint accounts that
  // are not the payee. Removing one drops it from the list the save persists — so it stops
  // being offered on every reopen — and hands the selection to the first remaining account
  // when the removed one was the chosen one (never leave a payment silently unassigned).
  const removeIban = (index: number) => {
    if (!canPickIban) return
    const nextIbans = recognizedIbans.filter((_, i) => i !== index)
    const nextAccounts = alignedAccounts(list => list.filter((_, i) => i !== index))
    const wasSelected = normalizeIban(recognizedIbans[index]) === normalizedPrimaryIban
    onChange({
      payeeIbans: nextIbans,
      // Dropping the row at the SAME index is what keeps the two lists aligned; leaving it
      // behind would shift every following bank line onto the wrong account.
      ...(nextAccounts ? { payeeAccounts: nextAccounts } : {}),
      iban: wasSelected ? (nextIbans[0] ?? null) : (data.iban ?? null),
    })
  }

  // The reviewer's own value is not part of the recognized list, so removing its card only
  // clears the choice.
  const removeCustomIban = () => {
    if (!canPickIban) return
    onChange({ iban: null })
  }

  const choiceCards = recognizedIbans.map((recognizedIban, index) => ({
    key: recognizedIban,
    index,
    value: recognizedIban,
  }))
  // The reviewer's value is not among the recognized accounts (legacy row, or typed
  // before this choice existed) — give it its own card instead of hiding it.
  if (normalizedPrimaryIban && !choiceCards.some(card => normalizeIban(card.value) === normalizedPrimaryIban)) {
    choiceCards.push({ key: CUSTOM_IBAN_KEY, index: -1, value: data.iban ?? '' })
  }
  // One recognized account (or none) needs no choice — a single labelled field says it all.
  const showIbanChoice = recognizedIbans.length > 1
  // …but the reviewer must still be able to get back to that one account after clearing or
  // overtyping the field, which the choice UI would otherwise have been their only route to.
  const loneRecognizedIban = recognizedIbans.length === 1 ? recognizedIbans[0] : null
  const adoptableIban = loneRecognizedIban
    && canPickIban
    && normalizeIban(loneRecognizedIban) !== normalizedPrimaryIban
    ? loneRecognizedIban
    : null

  const openAmountValue = data.openAmount === null || data.openAmount === undefined ? '' : `${data.openAmount}`

  // The paid status as a chip next to the title (same chip style as the "Offen"
  // receipt-state badge) — a quick  read; the select below stays editable.
  const statusBadge = data.paymentStatus && STATUS_BADGE[data.paymentStatus]
    ? (
      <Badge
        color={STATUS_BADGE[data.paymentStatus].color}
        label={fields.statusOptions[data.paymentStatus] ?? data.paymentStatus}
        customClass={classes.statusChip}
      />
    )
    : null

  const singleIbanCheck = ibanCheckFor(data.iban ?? '', true)
  // One recognized account gets no choice UI — but a lone foreign-currency account is
  // exactly as worth checking as one among several, so the hint belongs under this field
  // too. Only while the field actually holds that account: once the reviewer overtypes it,
  // the ingested currency no longer describes what is on screen.
  const singleCurrencyCheck = !showIbanChoice
    && loneRecognizedIban
    && normalizeIban(loneRecognizedIban) === normalizedPrimaryIban
    ? currencyCheckFor(0)
    : null

  // The confirmed IBAN does not stay on the document: the save appends it to the linked
  // supplier/customer, which is what makes the next invoice from that party resolve by IBAN.
  const storedWithPartyHint = data.iban
    ? (
      <span className={classes.debitHint} data-testid={'review-payment-iban-stored'}>
        {ibanChoiceLang.storedWithParty}
      </span>
    )
    : null

  // Reassurance only — the account itself is the editable field below, so the old
  // old read-only direct-debit-account echo is gone. serialize.js recomputes the match from the
  // STORED list, which is one save behind the reviewer's typing: drop the hint as soon as
  // the matched account is no longer among the current values, instead of claiming a match
  // for an IBAN that is no longer on screen. It returns on reload if it still matches.
  const debitMatchStillListed = !!debitMatchIban
    && debitIbans.some(iban => normalizeIban(iban) === normalizeIban(debitMatchIban))
  const debitMatchHint = debitMatchStillListed
    ? (
      <span className={clsx(classes.debitHint, classes.debitHintMatch)} data-testid={'review-payment-debit-match'}>
        {lang.documents.reviewPaymentStatus.debitAccountMatch(formatIban(debitMatchIban!))}
      </span>
    )
    : null

  // Always at least one row, so an invoice whose scan found no debit account still offers
  // the reviewer can enter one below — "editable like any other review field" means the
  // field exists when it is empty, not only when the analysis pre-filled it.
  const debitIbanRows = debitIbans.length ? debitIbans : ['']

  // Clearing a row deletes that entry (same semantics as removeIban on the payee side);
  // the rows recompute to a single empty input, so the field never disappears mid-edit.
  const editDebitIban = (index: number, value: string) => {
    if (readOnly) return
    const next = debitIbanRows
      .map((iban, i) => i === index ? value : iban)
      .filter(iban => iban.trim())
    onChange({ debitIbans: next.length ? next : null })
  }

  return (
    <div className={classes.root} data-testid={'review-payment-fields'}>
      <div className={classes.titleRow}>
        <span className={classes.title}>{fields.sectionTitle}</span>
        {statusBadge}
      </div>

      <div className={classes.rows}>
        <div className={classes.grid}>
          <div className={classes.field}>
            {renderLabel('paymentStatus', fields.status)}
            <select
              className={classes.select}
              data-testid={'review-payment-status'}
              disabled={readOnly}
              value={data.paymentStatus ?? ''}
              onChange={(e) => onChange({ paymentStatus: e.target.value || null })}
            >
              <option value={''}>{fields.none}</option>
              {PAYMENT_STATUS_CODES.map((code) => (
                <option key={code} value={code}>{fields.statusOptions[code]}</option>
              ))}
            </select>
          </div>

          <div className={classes.field}>
            {renderLabel('openAmount', fields.openAmount)}
            <TextInput
              name='openAmount'
              dataQsId='review-payment-openAmount'
              value={openAmountValue}
              readOnly={readOnly}
              onChange={(value: string) => {
                if (value === '') { onChange({ openAmount: null }); return }
                const parsed = Number(value.replace(',', '.'))
                // Ignore non-numeric input instead of writing NaN (which JSON-serializes to null
                // and silently drops the typed value).
                if (Number.isNaN(parsed)) return
                onChange({ openAmount: parsed })
              }}
            />
          </div>
        </div>

        <div className={classes.field}>
          {renderLabel('paymentMethodCode', fields.method)}
          <select
            className={classes.select}
            data-testid={'review-payment-method'}
            disabled={readOnly}
            value={data.paymentMethodCode ?? ''}
            onChange={(e) => handleMethodChange(e.target.value || null)}
          >
            <option value={''}>{fields.none}</option>
            {PAYMENT_METHOD_CODES.map((code) => (
              <option key={code} value={code}>{lang.documents.reviewPaymentStatus.paymentMethods[code] ?? code}</option>
            ))}
          </select>
        </div>

        {showIbanChoice
          ? (
            <div className={classes.field} data-testid={'review-payment-iban-choice'}>
              <div className={classes.choiceBanner}>
                <WarningAmberRoundedIcon className={classes.choiceBannerIcon} />
                <span>
                  {ibanChoiceLang.bannerPrefix}
                  <strong>{ibanChoiceLang.bannerCount(recognizedIbans.length)}</strong>
                  {ibanChoiceLang.bannerSuffix}
                </span>
              </div>
              <div
                className={classes.choiceList}
                role='radiogroup'
                aria-label={ibanChoiceLang.ariaLabel}
              >
                {choiceCards.map((card) => {
                  const selected = normalizeIban(card.value) === normalizedPrimaryIban
                  const { treatment, note } = ibanCheckFor(card.value, selected)
                  const isCustom = card.index < 0
                  const cardMeta = bankMetaFor(card.index, selected)
                  const currencyCheck = currencyCheckFor(card.index)
                  return (
                    <label
                      // Position, not value: the input keeps its DOM node (and the caret)
                      // while the reviewer corrects the IBAN it holds.
                      key={isCustom ? CUSTOM_IBAN_KEY : card.index}
                      className={clsx(
                        classes.choiceCard,
                        selected && classes.choiceCardSelected,
                        !canPickIban && classes.choiceCardReadOnly,
                      )}
                      data-testid={`review-payment-iban-option-${card.key}`}
                      data-selected={selected ? 'true' : 'false'}
                    >
                      <input
                        type='radio'
                        className={classes.choiceRadio}
                        name='review-payment-iban-choice'
                        value={card.value}
                        checked={selected}
                        disabled={!canPickIban}
                        onChange={() => selectIban(card.value, card.index)}
                      />
                      <div className={classes.choiceBody}>
                        <TextInput
                          name={`iban-${card.key}`}
                          dataQsId={`review-payment-iban-${card.key}`}
                          value={card.value}
                          readOnly={!canPickIban}
                          customClass={ibanFieldClass(treatment)}
                          onChange={(value: string) => isCustom ? selectIban(value) : editIban(card.index, value)}
                        />
                        {renderIbanCheck(treatment, note, `review-payment-iban-check-${card.key}`)}
                        {currencyCheck && renderIbanCheck(
                          currencyCheck.treatment,
                          currencyCheck.note,
                          `review-payment-iban-currency-${card.key}`,
                          currencyCheck.note,
                        )}
                        <div className={classes.choiceFooter}>
                          {cardMeta && (
                            <div className={classes.choiceMeta} data-testid={`review-payment-iban-meta-${card.key}`}>
                              {cardMeta}
                            </div>
                          )}
                          {canPickIban && (
                            <Tooltip title={ibanChoiceLang.deleteIban} arrow>
                              <button
                                type='button'
                                className={classes.deleteIban}
                                data-testid={`review-payment-iban-delete-${card.key}`}
                                aria-label={ibanChoiceLang.deleteIban}
                                // The button sits inside the card's <label>: a plain click
                                // would also activate the radio and select the very account
                                // being removed.
                                onClick={(event) => {
                                  event.preventDefault()
                                  event.stopPropagation()
                                  return isCustom ? removeCustomIban() : removeIban(card.index)
                                }}
                              >
                                <DeleteOutlineRoundedIcon className={classes.deleteIcon} />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </label>
                  )
                })}
              </div>
              {storedWithPartyHint}
            </div>
          )
          : (
            <div className={classes.field}>
              {renderLabel('iban', fields.iban)}
              <TextInput
                name='iban'
                dataQsId='review-payment-iban'
                value={data.iban ?? ''}
                readOnly={readOnly}
                customClass={ibanFieldClass(singleIbanCheck.treatment)}
                onChange={(value: string) => onChange({ iban: value || null })}
              />
              {renderIbanCheck(singleIbanCheck.treatment, singleIbanCheck.note, 'review-payment-iban-invalid')}
              {singleCurrencyCheck && renderIbanCheck(
                singleCurrencyCheck.treatment,
                singleCurrencyCheck.note,
                'review-payment-iban-currency',
                singleCurrencyCheck.note,
              )}
              {adoptableIban && (
                <button
                  type='button'
                  className={classes.adoptRecognized}
                  data-testid={'review-payment-iban-adopt'}
                  // Adopting the lone recognized account is the same act as picking a card:
                  // its ingested bank/BIC come with it.
                  onClick={() => selectIban(adoptableIban, 0)}
                >
                  {ibanChoiceLang.adoptRecognized(formatIban(adoptableIban))}
                </button>
              )}
              {storedWithPartyHint}
            </div>
          )}

        {/* FLAG:useDocuAi — Bank + BIC of the payment account, side by side under the IBAN
            they belong to. Both were display-only until 2026-08-11 (a bank line pinned to the
            first recognized IBAN): on a multi-account invoice that line can describe a
            different account than the one the reviewer picks, and nothing could correct it.
            Deliberately no client-side BIC format check — the server's isBicAllowNull already
            rejects a malformed value, and a second, stricter check here would block a save the
            server would have accepted. */}
        <div className={classes.grid}>
          <div className={classes.field}>
            {renderLabel('bankInstitute', fields.bankInstitute)}
            <TextInput
              name='bankInstitute'
              dataQsId='review-payment-bankInstitute'
              value={data.bankInstitute ?? ''}
              readOnly={readOnly}
              onChange={(value: string) => onChange({ bankInstitute: value || null })}
            />
          </div>

          <div className={classes.field}>
            {renderLabel('bic', fields.bic)}
            <TextInput
              name='bic'
              dataQsId='review-payment-bic'
              value={data.bic ?? ''}
              readOnly={readOnly}
              onChange={(value: string) => onChange({ bic: value || null })}
            />
          </div>
        </div>

        {/* FLAG:useDocuAi — the buyer-side direct-debit account. Its own field under the payee IBAN
            (never merged with it: on a direct debit the two are different parties' accounts).
            Deliberately no IBAN format/mod-97 check — these values are printed X-masked, so
            the check would flag every correct one. */}
        <div className={classes.field} data-testid={'review-payment-debit-ibans'}>
          {renderLabel('debitIbans', fields.debitIban)}
          {debitIbanRows.map((debitIban, index) => (
            <TextInput
              // Position, not value: the input keeps its DOM node (and the caret) while the
              // reviewer corrects the account it holds.
              key={index}
              name={`debitIban-${index}`}
              dataQsId={`review-payment-debit-iban-${index}`}
              value={debitIban}
              readOnly={readOnly}
              onChange={(value: string) => editDebitIban(index, value)}
            />
          ))}
          {debitMatchHint}
        </div>

        <div className={classes.grid}>
          <div className={classes.field}>
            <span className={classes.label}>{fields.cardNumber}</span>
            <TextInput
              name='paymentCardNumber'
              dataQsId='review-payment-cardNumber'
              value={data.paymentCardNumber ?? ''}
              readOnly={readOnly}
              onChange={(value: string) => onChange({ paymentCardNumber: value || null })}
            />
          </div>

          <div className={classes.field}>
            <span className={classes.label}>{fields.cardScheme}</span>
            <TextInput
              name='paymentCardScheme'
              dataQsId='review-payment-cardScheme'
              value={data.paymentCardScheme ?? ''}
              readOnly={readOnly}
              onChange={(value: string) => onChange({ paymentCardScheme: value || null })}
            />
          </div>
        </div>

        {/* v4 — moved out of the retired "Zahlungskonditionen" card, deliberately LAST: it is the
            one value here that is about when to pay rather than what the document states. */}
        <div className={classes.field} data-testid={'review-payment-dueDate'}>
          <DateInput
            name='dueDate'
            label={lang.receipt.form.dueDate}
            value={data.dueDate ? moment(data.dueDate).format(config.dateFormat.api) : ''}
            readOnly={readOnly}
            required={false}
            onChange={(date) => onChange({ dueDate: date ? moment(date).set('hour', 12).toDate() : null })}
          />
        </div>
      </div>
    </div>
  )
}

export default ReviewPaymentFields
